import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { NAV } from '../navConfig';

// ─────────────────────────────────────────────
// HEADER BACK BUTTON
// ─────────────────────────────────────────────
// Every screen's "Back" lives in one place: the top-left of the top bar.
//
// A page (or a nested sub-view) declares its back action by rendering
// <HeaderBack onClick={...} label="Back to X" /> wherever its old inline
// button used to sit. It renders nothing visible itself — it registers with
// the provider and the TopBar draws the button.
//
// When several are mounted at once (a page and a sub-view inside it), the one
// latest in document order wins: deeper / later views are the more specific
// ones, so their Back is the one the reader means.
//
// When none is mounted, the TopBar falls back to a route-derived parent
// (see getDefaultBack), so every card and sub-card gets a way out.

const HeaderBackContext = createContext(null);

export function HeaderBackProvider({ children }) {
  const entriesRef = useRef(new Map());
  const [version, setVersion] = useState(0);
  const bump = useCallback(() => setVersion(v => v + 1), []);

  const register = useCallback((id, entry) => {
    entriesRef.current.set(id, entry);
    bump();
  }, [bump]);

  const unregister = useCallback((id) => {
    entriesRef.current.delete(id);
    bump();
  }, [bump]);

  const value = useMemo(() => ({ register, unregister, entriesRef, version, bump }), [register, unregister, version, bump]);
  return <HeaderBackContext.Provider value={value}>{children}</HeaderBackContext.Provider>;
}

/** The registration the TopBar should draw, or null when none is mounted. */
export function useActiveHeaderBack() {
  const ctx = useContext(HeaderBackContext);
  if (!ctx) return null;
  const entries = [...ctx.entriesRef.current.values()].filter(e => e.markerRef.current?.isConnected);
  if (entries.length === 0) return null;
  entries.sort((a, b) => {
    const pos = a.markerRef.current.compareDocumentPosition(b.markerRef.current);
    // b after a → a sorts first, so the last item is latest in the document.
    return pos & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
  });
  return entries[entries.length - 1];
}

// Strips the arrows the old inline buttons carried in their text.
const cleanLabel = (node) => {
  if (typeof node === 'string') return node.replace(/^[\s←‹<]+/, '').trim();
  if (Array.isArray(node)) return node.map(cleanLabel).join('').trim();
  if (React.isValidElement(node)) return cleanLabel(node.props.children);
  return node == null || node === false ? '' : String(node);
};

let nextId = 0;

/**
 * Declares this view's Back action. Renders an invisible marker only.
 * Accepts the label either as `label` or as children (so old
 * <BackButton>← Back to X</BackButton> markup ports over unchanged).
 */
export function HeaderBack({ onClick, label, children, disabled = false }) {
  const ctx = useContext(HeaderBackContext);
  const idRef = useRef(null);
  if (idRef.current === null) idRef.current = `hb-${++nextId}`;
  const markerRef = useRef(null);
  const onClickRef = useRef(onClick);
  onClickRef.current = onClick;

  const text = cleanLabel(label ?? children) || 'Back';

  // Registered in a layout effect so the button is in the header on the
  // same paint as the view itself.
  useLayoutEffect(() => {
    if (!ctx) return undefined;
    ctx.register(idRef.current, {
      markerRef,
      label: text,
      disabled,
      onClick: (...args) => onClickRef.current?.(...args),
    });
    return undefined;
  }, [ctx?.register, text, disabled]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => { ctx?.unregister(idRef.current); }, [ctx?.unregister]); // eslint-disable-line react-hooks/exhaustive-deps

  return <span ref={markerRef} data-header-back="" hidden style={{ display: 'none' }} />;
}

// ─────────────────────────────────────────────
// ROUTE-DERIVED DEFAULT
// ─────────────────────────────────────────────
const NAV_ITEMS = NAV.flatMap(g => g.items);
const findNav = (id) => {
  for (const item of NAV_ITEMS) {
    if (item.id === id) return { item, parent: null };
    const child = (item.children || []).find(c => c.id === id);
    if (child) return { item: child, parent: item };
  }
  return null;
};

const EXTRA_LABELS = {
  alerts: 'Alerts',
  projects: 'Projects',
  'report-entry': 'Report Entry',
  'expense-master': 'Expense Master',
  'boq-hub': 'BOQ Hub',
  'qsi-hub': 'QS Hub',
  admin: 'Admin',
};

const labelFor = (id) => findNav(id)?.item.label || EXTRA_LABELS[id] || null;

// Where the top level of a module goes back to: its parent hub card when it
// has one, otherwise the dashboard of module cards.
const moduleParent = (id) => {
  const found = findNav(id);
  if (found?.parent) return { to: `/${found.parent.id}`, label: `Back to ${found.parent.label}` };
  return { to: '/dashboard', label: 'Back to Dashboard' };
};

/**
 * The parent screen for a path, or null when there is nowhere further up.
 * Pages whose sub-views are not addressable by URL register their own
 * <HeaderBack>, which takes precedence over this.
 */
export function getDefaultBack(pathname) {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0 || parts[0] === 'dashboard') return null;

  const root = parts[0];

  // A module's own "dashboard" tab is the module's front door.
  if (parts.length === 2 && parts[1] === 'dashboard') return moduleParent(root);

  if (parts.length === 1) return moduleParent(root);

  // Project workspace: every project section returns to that project.
  if (root === 'projects') {
    if (parts.length === 2) return { to: '/projects', label: 'Back to Projects' };
    return { to: `/projects/${parts[1]}`, label: 'Back to Project' };
  }

  if (root === 'admin') {
    if (parts[1] === 'projects' && parts[2]) return { to: `/admin?tab=projects&id=${parts[2]}`, label: 'Back to Project Setup' };
    if (parts[1] === 'users') return { to: '/admin?tab=users', label: 'Back to Users' };
    return { to: '/admin', label: 'Back to Admin' };
  }

  // BOQ hub: in Budget Master, /boq-hub/budget-master/project/:id has no …/project parent.
  if (root === 'boq-hub' && parts[1] === 'budget-master' && parts[2] === 'project' && parts.length === 4) {
    return { to: '/boq-hub/budget-master', label: 'Back to Budget Master' };
  }

  // Requisition edit has no /edit list of its own.
  if (root === 'enterprise-requisition' && parts[1] === 'edit') {
    return { to: '/enterprise-requisition/approved-requisitions', label: 'Back to Approved Requisitions' };
  }

  const to = `/${parts.slice(0, -1).join('/')}`;
  if (parts.length === 2) {
    return { to, label: `Back to ${labelFor(root) || 'Previous'}` };
  }
  return { to, label: 'Back' };
}

// ─────────────────────────────────────────────
// THE BUTTON (drawn by the TopBar)
// ─────────────────────────────────────────────
export function HeaderBackButton({ label, onClick, disabled }) {
  return (
    <button
      id="header-back-button"
      className="header-back-btn"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
    >
      <span className="header-back-btn__icon">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
      </span>
      <span className="header-back-btn__label hide-mobile">{label}</span>
    </button>
  );
}

export const HeaderBackStyles = () => (
  <style>{`
    .header-back-btn {
      display: inline-flex; align-items: center; gap: 8px;
      max-width: min(30vw, 300px);
      padding: 5px 14px 5px 5px;
      background: linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.04));
      border: 1px solid rgba(148,180,220,0.28);
      border-radius: 999px;
      color: #e6eef8; font-family: var(--font); font-size: 12.5px; font-weight: 700;
      cursor: pointer; white-space: nowrap;
      box-shadow: inset 0 1px 0 rgba(255,255,255,0.08), 0 4px 14px -6px rgba(0,0,0,0.5);
      transition: background 0.18s ease, border-color 0.18s ease, transform 0.12s ease;
      animation: header-back-in 0.24s cubic-bezier(0.22,1,0.36,1);
    }
    .header-back-btn:hover {
      background: linear-gradient(180deg, rgba(255,255,255,0.18), rgba(255,255,255,0.08));
      border-color: rgba(96,165,250,0.6);
    }
    .header-back-btn:hover .header-back-btn__icon { transform: translateX(-2px); }
    .header-back-btn:active { transform: translateY(1px); }
    .header-back-btn:focus-visible { outline: 2px solid #60a5fa; outline-offset: 2px; }
    .header-back-btn:disabled { opacity: 0.5; cursor: not-allowed; }
    .header-back-btn__icon {
      width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0;
      display: inline-flex; align-items: center; justify-content: center;
      background: linear-gradient(135deg, var(--brand, #2563eb), var(--brand-2, #22d3ee));
      color: #fff; box-shadow: 0 4px 10px -3px rgba(34,211,238,0.6);
      transition: transform 0.18s ease;
    }
    .header-back-btn__label { overflow: hidden; text-overflow: ellipsis; }
    @media (max-width: 768px) {
      .header-back-btn { padding: 4px; }
    }
    @keyframes header-back-in {
      from { opacity: 0; transform: translateX(-6px); }
      to { opacity: 1; transform: translateX(0); }
    }
  `}</style>
);
